// src/pages/MainPanel/CreateExperiment/components/ExprConfigPreviewPanel.jsx
import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Button,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PropTypes from "prop-types";
import { CODE_PALETTE_LIGHT as palette } from "../../../../theme/codeTheme";
import {
  HAIRLINE,
  SURFACE,
  SURFACE_MUTED,
  INK_SECONDARY,
  INK_MUTED,
  RADIUS,
  SHADOW_SM,
  FONT,
  WEIGHT,
  STATUS,
} from "../../../../theme/tokens";
import { toMessageList } from "./ExprConfigStates";

/** Classify a bare YAML value and return a colored token. */
function valueTokens(value) {
  if (!value) return [];
  if (/^(true|false|yes|no|null|~)$/i.test(value))
    return [{ color: palette.bool, text: value }];
  if (/^-?\d+(\.\d+)?$/.test(value))
    return [{ color: palette.number, text: value }];
  if (/^[[{]/.test(value)) return [{ color: palette.bracket, text: value }];
  return [{ color: palette.string, text: value }];
}

/**
 * Tokenize one YAML line into an array of { color, text } segments.
 * Handles: comments, key-value pairs, list items (with optional inline key).
 */
function tokenizeLine(line) {
  // Full-line comment
  if (/^\s*#/.test(line)) return [{ color: palette.comment, text: line }];

  // List item — "  - " optionally followed by a key-value pair
  const listMatch = line.match(/^(\s*)(- )(.*)$/);
  if (listMatch) {
    const [, indent, bullet, rest] = listMatch;
    const restKv = rest.match(/^([\w\-.]+)(:\s*)(.*)$/);
    if (restKv) {
      const [, key, colonSpace, val] = restKv;
      return [
        { color: palette.text, text: indent },
        { color: palette.text, text: bullet },
        { color: palette.key, text: key },
        { color: palette.text, text: colonSpace },
        ...valueTokens(val),
      ];
    }
    return [
      { color: palette.text, text: indent },
      { color: palette.text, text: bullet },
      ...valueTokens(rest),
    ];
  }

  // Key-value pair (key: alphanumeric / underscore / hyphen / dot)
  const kvMatch = line.match(/^(\s*)([\w\-.]+)(:\s*)(.*)$/);
  if (kvMatch) {
    const [, indent, key, colonSpace, value] = kvMatch;
    return [
      { color: palette.text, text: indent },
      { color: palette.key, text: key },
      { color: palette.text, text: colonSpace },
      ...valueTokens(value),
    ];
  }

  return [{ color: palette.text, text: line }];
}

const ConfigPanelRailBox = ({ title, icon, action, children, sx, bodySx }) => (
  <Box
    sx={{
      border: `1px solid ${HAIRLINE}`,
      borderRadius: `${RADIUS.md}px`,
      bgcolor: SURFACE,
      boxShadow: SHADOW_SM,
      overflow: "hidden",
      ...sx,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 1.75,
        py: 1,
        bgcolor: SURFACE_MUTED,
        borderBottom: `1px solid ${HAIRLINE}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {icon}
        <Typography
          sx={{
            fontSize: FONT.xs,
            fontWeight: WEIGHT.bold,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: INK_SECONDARY,
          }}
        >
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    <Box sx={bodySx}>{children}</Box>
  </Box>
);

ConfigPanelRailBox.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  action: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
  bodySx: PropTypes.object,
};

export const ConfigurationValidationBox = ({
  error,
  onErrorClose,
  success,
  onSuccessClose,
  warnings,
  onWarningClose,
}) => {
  const visibleErrors = toMessageList(error);
  const visibleWarnings = toMessageList(warnings);
  const hasWarnings = visibleWarnings.length > 0;
  const hasSuccess = !!success;
  const hasContent = visibleErrors.length > 0 || hasWarnings || hasSuccess;

  return (
    <ConfigPanelRailBox
      title="Configuration Validation"
      icon={<FactCheckOutlinedIcon sx={{ fontSize: 16, color: INK_MUTED }} />}
      sx={{ mb: 1.25 }}
      bodySx={{ p: 1.25 }}
    >
      {!hasContent && (
        <Typography
          sx={{
            fontSize: FONT.sm,
            color: INK_MUTED,
            py: 1,
            textAlign: "center",
          }}
        >
          No validation messages yet.
        </Typography>
      )}
      {visibleErrors.map((message, index) => (
        <Alert
          key={`${index}-${message}`}
          severity="error"
          sx={{
            mb:
              index < visibleErrors.length - 1 || hasWarnings || hasSuccess
                ? 1
                : 0,
          }}
          onClose={() => onErrorClose?.(index)}
        >
          {message}
        </Alert>
      ))}
      {visibleWarnings.map((warning, index) => (
        <Alert
          key={`${index}-${warning}`}
          severity="warning"
          sx={{
            mb: index < visibleWarnings.length - 1 || hasSuccess ? 1 : 0,
          }}
          onClose={() => onWarningClose?.(index)}
        >
          {warning}
        </Alert>
      ))}
      {success && (
        <Alert severity="success" sx={{ mb: 0 }} onClose={onSuccessClose}>
          {success}
        </Alert>
      )}
    </ConfigPanelRailBox>
  );
};

ConfigurationValidationBox.propTypes = {
  error: PropTypes.arrayOf(PropTypes.string),
  onErrorClose: PropTypes.func,
  success: PropTypes.string,
  onSuccessClose: PropTypes.func,
  warnings: PropTypes.arrayOf(PropTypes.string),
  onWarningClose: PropTypes.func,
};

/**
 * Sticky sidebar that renders a live YAML configuration preview as a clean,
 * slate/white code card with a header bar and copy-to-clipboard action.
 */
const ConfigPreviewPanel = ({ yaml, footer, validation = {} }) => {
  // Get rid of Windows newlines, similar to handleSaveEFabricConfig
  const text = (yaml ?? "").replace(/\r/g, "");
  const lines = text.split("\n");
  const isEmpty = !text.trim();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — silently ignore
    }
  };

  return (
    <Box
      sx={{
        // Wide (lg+): right sidebar. Narrow: full-width top bar.
        width: { xs: "100%", lg: 340 },
        flexShrink: 0,
        // sits below the form on narrow screens, sticky rail on wide screens
        order: { xs: 1, lg: 0 },
        position: { xs: "static", lg: "sticky" },
        top: 0,
        alignSelf: { xs: "stretch", lg: "flex-start" },
      }}
    >
      <ConfigurationValidationBox {...validation} />
      <ConfigPanelRailBox
        title="Configuration Preview"
        icon={<CodeIcon sx={{ fontSize: 16, color: INK_MUTED }} />}
        action={
          <Tooltip title={copied ? "Copied!" : "Copy YAML"}>
            <span>
              <IconButton
                size="small"
                onClick={handleCopy}
                disabled={isEmpty}
                aria-label="Copy YAML"
                sx={{ color: INK_MUTED }}
              >
                {copied ? (
                  <CheckIcon sx={{ fontSize: 16, color: STATUS.success }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        }
      >
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.75,
            bgcolor: palette.bg,
            fontSize: "0.72rem",
            fontFamily: "monospace",
            lineHeight: 1.7,
            whiteSpace: "pre",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: { xs: 200, lg: "calc(100vh - 260px)" },
            color: isEmpty ? INK_MUTED : palette.text,
          }}
        >
          {isEmpty
            ? "# Fill in the form to preview your\n# workspace configuration."
            : lines.map((line, i) => (
                <span key={i}>
                  {tokenizeLine(line).map((tok, j) => (
                    <span key={j} style={{ color: tok.color }}>
                      {tok.text}
                    </span>
                  ))}
                  {i < lines.length - 1 && "\n"}
                </span>
              ))}
        </Box>
      </ConfigPanelRailBox>
      {footer ? <Box sx={{ mt: 1.25 }}>{footer}</Box> : null}
    </Box>
  );
};

ConfigPreviewPanel.propTypes = {
  yaml: PropTypes.string,
  footer: PropTypes.node,
  validation: PropTypes.shape({
    error: PropTypes.arrayOf(PropTypes.string),
    onErrorClose: PropTypes.func,
    success: PropTypes.string,
    onSuccessClose: PropTypes.func,
    warnings: PropTypes.arrayOf(PropTypes.string),
    onWarningClose: PropTypes.func,
  }),
};

export default ConfigPreviewPanel;

export const ConfigUploadFooter = ({ onUploadClick }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      width: "90%",
      ml: "auto",
    }}
  >
    <Button
      variant="outlinedButton"
      fullWidth
      startIcon={<UploadFileIcon />}
      onClick={onUploadClick}
    >
      Upload efabric.yml
    </Button>
    <Tooltip title="Upload an efabric.yml to reproduce the configuration">
      <IconButton
        aria-label="Upload efabric.yml help"
        size="small"
        sx={{
          border: (theme) => `1px solid ${theme.palette.divider}`,
          color: "text.secondary",
          flexShrink: 0,
        }}
      >
        <InfoOutlinedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  </Box>
);

ConfigUploadFooter.propTypes = {
  onUploadClick: PropTypes.func.isRequired,
};
